import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { responsiveGridColumns } from "../../../core/breakpoints";
import { C } from "../../../core/constants";
import { localizedPath } from "../../../core/localePaths";
import { useI18n } from "../../i18n/LocaleProvider";
import { APP_FONT_SIZE } from "../../../core/theme";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { buildChineseZodiacPdfHtml } from "../../export/chineseZodiacPdf";
import { createPdfFileName, printHtmlAsPdf } from "../../export/pdfUtils";
import { createChineseGeneratePrediction, fetchPredictionById } from "../../predictions/api";
import { toPredictionAccessPopup } from "../../predictions/accessError";
import { getFreeQuotaPopupCopy, shouldShowFreeQuotaPopup } from "../../predictions/freeQuota";
import { applyPredictionUserUpdate } from "../../predictions/postSuccessUserUpdate";
import {
  buildDailyReading,
  buildDestinySynthesisV2,
  computeBaZiProfile,
  getHourAnimalIndex,
  HOUR_PERIODS,
  normalizeIsoDate,
  parseIsoDate,
  SDK_ENGINE,
  todayIsoDate,
} from "./chineseZodiacEngine";
import Button from "../../../ui/Button";
import FreeQuotaConfirmPopup from "../../../ui/FreeQuotaConfirmPopup";
import GlassCard from "../../../ui/GlassCard";
import PredictionAccessPopup from "../../../ui/PredictionAccessPopup";

const CZ_DATA = [
  { emoji: "🐀", name: "Rat", element: "Water", traitKeys: ["cz_trait_clever", "cz_trait_adaptable", "cz_trait_quick_witted", "cz_trait_resourceful"], luckyColorKeys: ["cz_color_blue", "cz_color_gold", "cz_color_green"], avoidColorKeys: ["cz_color_yellow", "cz_color_brown"], compatible: ["Dragon", "Monkey", "Ox"] },
  { emoji: "🐂", name: "Ox", element: "Earth", traitKeys: ["cz_trait_diligent", "cz_trait_reliable", "cz_trait_determined", "cz_trait_honest"], luckyColorKeys: ["cz_color_white", "cz_color_yellow", "cz_color_green"], avoidColorKeys: ["cz_color_blue", "cz_color_red"], compatible: ["Snake", "Rooster", "Rat"] },
  { emoji: "🐯", name: "Tiger", element: "Wood", traitKeys: ["cz_trait_brave", "cz_trait_confident", "cz_trait_charismatic", "cz_trait_competitive"], luckyColorKeys: ["cz_color_blue", "cz_color_grey", "cz_color_orange"], avoidColorKeys: ["cz_color_gold", "cz_color_silver"], compatible: ["Horse", "Dog", "Pig"] },
  { emoji: "🐇", name: "Rabbit", element: "Wood", traitKeys: ["cz_trait_graceful", "cz_trait_artistic", "cz_trait_diplomatic", "cz_trait_calm"], luckyColorKeys: ["cz_color_red", "cz_color_pink", "cz_color_purple"], avoidColorKeys: ["cz_color_dark_brown", "cz_color_dark_yellow"], compatible: ["Goat", "Pig", "Dog"] },
  { emoji: "🐉", name: "Dragon", element: "Earth", traitKeys: ["cz_trait_visionary", "cz_trait_powerful", "cz_trait_passionate", "cz_trait_generous"], luckyColorKeys: ["cz_color_gold", "cz_color_silver", "cz_color_white"], avoidColorKeys: ["cz_color_blue", "cz_color_green"], compatible: ["Monkey", "Rat", "Rooster"] },
  { emoji: "🐍", name: "Snake", element: "Fire", traitKeys: ["cz_trait_wise", "cz_trait_mysterious", "cz_trait_intuitive", "cz_trait_elegant"], luckyColorKeys: ["cz_color_black", "cz_color_red", "cz_color_yellow"], avoidColorKeys: ["cz_color_white", "cz_color_gold"], compatible: ["Ox", "Rooster", "Monkey"] },
  { emoji: "🐴", name: "Horse", element: "Fire", traitKeys: ["cz_trait_free_spirited", "cz_trait_energetic", "cz_trait_perceptive", "cz_trait_enthusiastic"], luckyColorKeys: ["cz_color_yellow", "cz_color_green", "cz_color_red"], avoidColorKeys: ["cz_color_white", "cz_color_blue"], compatible: ["Tiger", "Goat", "Dog"] },
  { emoji: "🐑", name: "Goat", element: "Earth", traitKeys: ["cz_trait_creative", "cz_trait_gentle", "cz_trait_compassionate", "cz_trait_thoughtful"], luckyColorKeys: ["cz_color_brown", "cz_color_red", "cz_color_purple"], avoidColorKeys: ["cz_color_blue", "cz_color_black"], compatible: ["Rabbit", "Horse", "Pig"] },
  { emoji: "🐒", name: "Monkey", element: "Metal", traitKeys: ["cz_trait_inventive", "cz_trait_playful", "cz_trait_versatile", "cz_trait_witty"], luckyColorKeys: ["cz_color_white", "cz_color_blue", "cz_color_gold"], avoidColorKeys: ["cz_color_red", "cz_color_pink"], compatible: ["Rat", "Dragon", "Snake"] },
  { emoji: "🐓", name: "Rooster", element: "Metal", traitKeys: ["cz_trait_observant", "cz_trait_hardworking", "cz_trait_courageous", "cz_trait_confident"], luckyColorKeys: ["cz_color_gold", "cz_color_brown", "cz_color_yellow"], avoidColorKeys: ["cz_color_red", "cz_color_green"], compatible: ["Ox", "Snake", "Dragon"] },
  { emoji: "🐕", name: "Dog", element: "Earth", traitKeys: ["cz_trait_loyal", "cz_trait_honest", "cz_trait_courageous", "cz_trait_reliable"], luckyColorKeys: ["cz_color_red", "cz_color_green", "cz_color_purple"], avoidColorKeys: ["cz_color_blue", "cz_color_gold"], compatible: ["Tiger", "Rabbit", "Horse"] },
  { emoji: "🐖", name: "Pig", element: "Water", traitKeys: ["cz_trait_generous", "cz_trait_diligent", "cz_trait_compassionate", "cz_trait_sincere"], luckyColorKeys: ["cz_color_yellow", "cz_color_grey", "cz_color_brown"], avoidColorKeys: ["cz_color_red", "cz_color_blue"], compatible: ["Goat", "Tiger", "Rabbit"] },
];
const ELEM_COLORS = { Wood: "#6abf69", Fire: "#ff7043", Earth: "#c8a96e", Metal: "#b0bec5", Water: "#4fc3f7" };
const PILLAR_LABEL_KEYS = ["cz_pillar_year", "cz_pillar_month", "cz_pillar_day", "cz_pillar_hour"];
const PILLAR_ROLE_KEYS = ["cz_pillar_role_year", "cz_pillar_role_month", "cz_pillar_role_day", "cz_pillar_role_hour"];
const PILLAR_DESC_KEYS = ["cz_pillar_desc_year", "cz_pillar_desc_month", "cz_pillar_desc_day", "cz_pillar_desc_hour"];
const BIRTH_HOUR_VALUES = Array.from({ length: 24 }, (_, h) => h);
const MONTH_LABEL_KEYS = ["month_jan", "month_feb", "month_mar", "month_apr", "month_may", "month_jun", "month_jul", "month_aug", "month_sep", "month_oct", "month_nov", "month_dec"];
const CZ_COLOR_HEX = {
  cz_color_blue: "#64b5f6",
  cz_color_gold: "#ffd54f",
  cz_color_green: "#81c784",
  cz_color_yellow: "#fff176",
  cz_color_brown: "#bc8f6f",
  cz_color_white: "#f5f5f5",
  cz_color_red: "#ef9a9a",
  cz_color_grey: "#b0bec5",
  cz_color_orange: "#ffb74d",
  cz_color_silver: "#cfd8dc",
  cz_color_pink: "#f48fb1",
  cz_color_purple: "#ce93d8",
  cz_color_dark_brown: "#8d6e63",
  cz_color_dark_yellow: "#d4b94f",
  cz_color_black: "#90a4ae",
};

function getHourPeriodIndex(hour) {
  const normalizedHour = ((Number(hour) % 24) + 24) % 24;
  return Math.floor((normalizedHour + 1) / 2) % 12;
}

function mapBirthTimeToDefaultHour(hour, minute = 0) {
  const normalizedHour = ((Number(hour) % 24) + 24) % 24;
  const normalizedMinute = Math.max(0, Math.min(59, Number(minute) || 0));
  if (normalizedMinute === 0) return normalizedHour;
  // For non-zero minutes, keep the selection anchored to the current 2-hour zodiac slot.
  // Example: 7:30 and 8:30 both map to 8 (slot 7:00-9:00), 9:30 maps to 10.
  return (Math.floor((normalizedHour + 1) / 2) * 2) % 24;
}

function getChineseBirthDefaults(authUser) {
  const now = new Date();
  const fallback = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: 0,
  };

  const birthDateRaw = String(authUser?.birthDate || "").trim();
  let year = fallback.year;
  let month = fallback.month;
  let day = fallback.day;

  const dateMatch = birthDateRaw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateMatch) {
    const parsedYear = Number(dateMatch[1]);
    const parsedMonth = Number(dateMatch[2]);
    const parsedDay = Number(dateMatch[3]);
    if (Number.isInteger(parsedYear) && parsedYear >= 1900 && parsedYear <= now.getFullYear()) year = parsedYear;
    if (Number.isInteger(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) month = parsedMonth;
    const maxDay = new Date(year, month, 0).getDate();
    if (Number.isInteger(parsedDay) && parsedDay >= 1 && parsedDay <= maxDay) day = parsedDay;
  }

  const birthTimeRaw = String(authUser?.birthTime || "").trim();
  const timeMatch = birthTimeRaw.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  let hour = 0; // default zodiac slot representative for 23:00-01:00
  if (timeMatch) {
    const parsedHour = Number(timeMatch[1]);
    const parsedMinute = Number(timeMatch[2] || 0);
    if (
      Number.isInteger(parsedHour) &&
      Number.isInteger(parsedMinute) &&
      parsedHour >= 0 &&
      parsedHour <= 23 &&
      parsedMinute >= 0 &&
      parsedMinute <= 59
    ) {
      hour = mapBirthTimeToDefaultHour(parsedHour, parsedMinute);
    }
  }

  return { year, month, day, hour };
}

function interpolate(template, vars = {}) {
  return String(template || "").replace(/%\{(\w+)\}/g, (_, key) => (
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : `%{${key}}`
  ));
}

function slugify(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function localizeCzData(data, t, locale) {
  return data.map((sign) => {
    const signKey = slugify(sign.name);
    const localizedEmoji = locale === "vi" && signKey === "rabbit" ? "🐈" : sign.emoji;
    return {
      ...sign,
      emoji: localizedEmoji,
      label: t(`cz_sign_${signKey}_name`),
      desc: t(`cz_sign_${signKey}_desc`),
      traits: sign.traitKeys.map((key) => t(key)),
      lucky: sign.luckyColorKeys.map((key) => t(key)),
      avoid: sign.avoidColorKeys.map((key) => t(key)),
      luckyColorKeys: sign.luckyColorKeys,
      avoidColorKeys: sign.avoidColorKeys,
    };
  });
}

function findSignByName(data, name) {
  return data.find((item) => item.name === name) || null;
}

function getElementLabel(t, element) {
  return t(`cz_element_${slugify(element)}`);
}

function getRecentYearsForSign(name, currentYear, count = 4) {
  const signIndex = CZ_DATA.findIndex((item) => item.name === name);
  if (signIndex < 0) return [];
  const baseYear = 1924 + signIndex;
  const latestCycle = Math.floor((currentYear - baseYear) / 12);
  return Array.from({ length: count }, (_, i) => baseYear + 12 * (latestCycle - i)).filter((year) => year <= currentYear).reverse();
}

const ChineseSignCell = memo(function ChineseSignCell({ sign, index, elementColor, elementLabel, onSelect }) {
  return (
    <div
      className="ecliptica-chinese-sign-cell"
      onClick={() => onSelect(index)}
      style={{ "--sign-element-color": elementColor }}
    >
      <div style={{ fontSize: 30, marginBottom: 6 }}>{sign.emoji}</div>
      <div style={{ fontSize: 13, color: C.textSecondary }}>{sign.label || sign.name}</div>
      <div style={{ fontSize: 10, color: elementColor, marginTop: 3 }}>{elementLabel}</div>
    </div>
  );
});

function PillarCard({ pillar, index, fs, revealed, t, pillarLabels, onClick }) {
  const ec = ELEM_COLORS[pillar.element];
  const delay = index * 120;
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      style={{
        background: `linear-gradient(160deg, ${ec}14, rgba(255,255,255,0.02))`,
        border: `1px solid ${ec}55`,
        borderRadius: 20,
        padding: 28,
        position: "relative",
        overflow: "hidden",
        boxShadow: isHovered ? `0 0 44px ${ec}44` : `0 0 30px ${ec}22`,
        opacity: revealed ? 1 : 0,
        transform: revealed
          ? (isHovered ? "translateY(-4px) scale(1.015)" : "translateY(0) scale(1)")
          : "translateY(20px) scale(0.96)",
        transition: `all 0.6s cubic-bezier(0.34,1.56,0.64,1) ${delay}ms`,
        cursor: "pointer",
      }}
    >
      <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: `radial-gradient(circle,${ec}22,transparent 70%)`, pointerEvents: "none" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>{interpolate(t("cz_pillar_label_with_suffix"), { label: pillarLabels[index] })}</div>
          <div style={{ fontSize: 11, color: ec, letterSpacing: "0.06em" }}>{pillar.role.split("·")[0].trim()}</div>
        </div>
        <div style={{ fontSize: 28, color: ec, opacity: 0.6, fontFamily: "serif" }}>{pillar.chinese}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 52, lineHeight: 1, filter: `drop-shadow(0 0 16px ${ec}99)`, animation: "float-up 4s ease-in-out infinite", animationDelay: `${index * 0.5}s` }}>
          {pillar.animal.emoji}
        </div>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, color: C.textPrimary, lineHeight: 1.1 }}>{pillar.animal.label || pillar.animal.name}</div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 6, padding: "3px 10px", borderRadius: 12, background: `${ec}18`, border: `1px solid ${ec}44` }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: ec }} />
            <span style={{ fontSize: 11, color: ec, fontWeight: 500 }}>{getElementLabel(t, pillar.element)}</span>
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>
        <span style={{ color: ec }}>◈</span> {pillar.period}
      </div>
      <p style={{ fontSize: fs - 2, color: C.textSecondary, lineHeight: 1.65 }}>{pillar.desc}</p>
    </div>
  );
}

export default function ChineseZodiacPage({ acc, t, replayPredictionId, onCreditsUpdate, authUser, onResetPage }) {
  const { locale } = useI18n();
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const tr = (key, vars = {}) => interpolate(t(key), vars);
  const currentYear = new Date().getFullYear();
  const czData = useMemo(() => localizeCzData(CZ_DATA, t, locale), [t, locale]);
  const getSignLabel = (name) => findSignByName(czData, name)?.label || name;
  const getLocalizedElementLabel = (element) => getElementLabel(t, element);
  const chineseCreditCostRaw = Number(authUser?.predictionCosts?.chinese);
  const chineseCreditCost = Number.isFinite(chineseCreditCostRaw) && chineseCreditCostRaw > 0 ? chineseCreditCostRaw : null;
  const revealButtonLabel = chineseCreditCost
    ? tr("cz_reveal_four_pillars_cost_dynamic", { credits: chineseCreditCost })
    : t("cz_reveal_four_pillars");
  const pillarLabels = PILLAR_LABEL_KEYS.map((key) => t(key));
  const pillarRoles = PILLAR_ROLE_KEYS.map((key) => t(key));
  const pillarDescriptions = PILLAR_DESC_KEYS.map((key) => t(key));
  const monthLabels = MONTH_LABEL_KEYS.map((key) => t(key));
  const compatibilityTiers = [t("cz_compat_tier_1"), t("cz_compat_tier_2"), t("cz_compat_tier_3")];
  const defaultBirth = getChineseBirthDefaults(authUser);
  const [year, setYear] = useState(defaultBirth.year);
  const [month, setMonth] = useState(defaultBirth.month);
  const [day, setDay] = useState(defaultBirth.day);
  const [hour, setHour] = useState(defaultBirth.hour);
  const [revealed, setRevealed] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [pillarsReady, setPillarsReady] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [readingDate, setReadingDate] = useState(() => todayIsoDate());
  const [dailyReading, setDailyReading] = useState(null);
  const [loadingReplay, setLoadingReplay] = useState(() => Boolean(replayPredictionId));
  const [showFreeQuotaPopup, setShowFreeQuotaPopup] = useState(false);
  const [pendingPredictionResponse, setPendingPredictionResponse] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGenerateLockedByEntitlement, setIsGenerateLockedByEntitlement] = useState(false);
  const [showAccessPopup, setShowAccessPopup] = useState(false);
  const [accessPopupCopy, setAccessPopupCopy] = useState({ title: "", message: "" });
  const isReplayMode = Boolean(replayPredictionId);

  const openDetailModal = useCallback((payload) => {
    setDetailModal(payload);
  }, []);

  const closeDetailModal = useCallback(() => {
    setDetailModal(null);
  }, []);

  const buildDailyReadingForPillars = useCallback((pillars, dateIso = todayIsoDate()) => {
    const parts = parseIsoDate(dateIso) || parseIsoDate(todayIsoDate());
    if (!parts || !pillars?.length) return null;
    return buildDailyReading({
      readingYear: parts.year,
      readingMonth: parts.month,
      readingDay: parts.day,
      pillars,
      anchorSignName: pillars[2]?.animal?.name,
      czData,
      t: tr,
      getSignLabel,
      getElementLabel: getLocalizedElementLabel,
    });
  }, [czData, tr, getSignLabel, getLocalizedElementLabel]);

  const formatReadingDateLabel = useCallback((dateIso) => {
    const parts = parseIsoDate(dateIso);
    if (!parts) return dateIso;
    const dateObj = new Date(parts.year, parts.month - 1, parts.day);
    return dateObj.toLocaleDateString(locale === "vi" ? "vi-VN" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [locale]);

  async function runReveal() {
    if (isSubmitting || animating || isGenerateLockedByEntitlement) return;
    const computedPillars = computeBaZiProfile({
      year,
      month,
      day,
      hour,
      czData,
      pillarRoles,
      pillarDescriptions,
    });
    const readingDateForSession = todayIsoDate();
    setReadingDate(readingDateForSession);
    const computedReading = buildDailyReadingForPillars(computedPillars, readingDateForSession);

    if (!isReplayMode) {
      setIsSubmitting(true);
      try {
        const predictionResponse = await createChineseGeneratePrediction({
          input_payload: { year, month, day, hour, reading_date: readingDateForSession, locale },
          result_payload: {
            generated_from: "chinese_generate",
            engine: SDK_ENGINE,
            daily_reading: computedReading,
            replay_seed: { year, month, day, hour, reading_date: readingDateForSession, locale },
          },
        });
        setPendingPredictionResponse(predictionResponse);
      } catch (error) {
        const popupPayload = toPredictionAccessPopup(error, t);
        if (popupPayload.shouldShowPopup) {
          setAccessPopupCopy({ title: popupPayload.title, message: popupPayload.message });
          setShowAccessPopup(true);
          setIsGenerateLockedByEntitlement(true);
        }
        return;
      } finally {
        setIsSubmitting(false);
      }
    }
    setIsGenerateLockedByEntitlement(false);
    setRevealed(null);
    setDailyReading(null);
    setPillarsReady(false);
    setAnimating(true);
    setTimeout(() => {
      setRevealed(computedPillars);
      setDailyReading(computedReading);
      setDetailModal(null);
      setAnimating(false);
      setTimeout(() => setPillarsReady(true), 100);
    }, 800);
  }

  function requestReveal({ skipFreeQuotaGuard = false } = {}) {
    if (animating || isSubmitting || isGenerateLockedByEntitlement) return;
    if (!isReplayMode && !skipFreeQuotaGuard && shouldShowFreeQuotaPopup(authUser, "chinese")) {
      setShowFreeQuotaPopup(true);
      return;
    }
    runReveal();
  }

  useEffect(() => {
    if (!replayPredictionId) return;
    let cancelled = false;
    async function loadReplayPrediction() {
      setLoadingReplay(true);
      try {
        const response = await fetchPredictionById(replayPredictionId);
        if (cancelled) return;
        const prediction = response?.prediction;
        if (!prediction || prediction.feature !== "chinese") return;
        const payload = prediction.input_payload || {};
        const replayYear = Number(payload.year || year);
        const replayMonth = Number(payload.month || month);
        const replayDay = Number(payload.day || day);
        const replayHour = Number(payload.hour || hour);
        const replayBirthIso = normalizeIsoDate(`${replayYear}-${String(replayMonth).padStart(2, "0")}-${String(replayDay).padStart(2, "0")}`);
        const savedDailyDate = normalizeIsoDate(prediction.result_payload?.daily_reading?.date);
        const replayReadingDate = normalizeIsoDate(payload.reading_date)
          || normalizeIsoDate(prediction.created_at)
          || (savedDailyDate && savedDailyDate !== replayBirthIso ? savedDailyDate : null)
          || todayIsoDate();
        const replayPillars = computeBaZiProfile({
          year: replayYear,
          month: replayMonth,
          day: replayDay,
          hour: replayHour,
          czData,
          pillarRoles,
          pillarDescriptions,
        });
        setReadingDate(replayReadingDate);
        setYear(replayYear);
        setMonth(replayMonth);
        setDay(replayDay);
        setHour(replayHour);
        setDailyReading(buildDailyReadingForPillars(replayPillars, replayReadingDate));
        setRevealed(replayPillars);
        setDetailModal(null);
        setAnimating(false);
        setPillarsReady(true);
      } catch (_) {
      } finally {
        if (!cancelled) setLoadingReplay(false);
      }
    }
    loadReplayPrediction();
    return () => {
      cancelled = true;
    };
  }, [replayPredictionId, locale, t]);

  useEffect(() => {
    if (replayPredictionId) return;
    const nextDefaults = getChineseBirthDefaults(authUser);
    setYear(nextDefaults.year);
    setMonth(nextDefaults.month);
    setDay(nextDefaults.day);
    setHour(nextDefaults.hour);
    setDetailModal(null);
    setDailyReading(null);
  }, [authUser, replayPredictionId]);

  useEffect(() => {
    if (isReplayMode || !pendingPredictionResponse || animating || !revealed) return;
    applyPredictionUserUpdate({ authUser, predictionResponse: pendingPredictionResponse, onCreditsUpdate });
    setPendingPredictionResponse(null);
  }, [isReplayMode, pendingPredictionResponse, animating, revealed, authUser, onCreditsUpdate]);

  useEffect(() => {
    if (isReplayMode) return;
    if (!isGenerateLockedByEntitlement) return;
    if (!shouldShowFreeQuotaPopup(authUser, "chinese")) {
      setIsGenerateLockedByEntitlement(false);
    }
  }, [authUser, isGenerateLockedByEntitlement, isReplayMode]);

  const localizedRevealed = revealed
    ? revealed.map((pillar, index) => {
      const localizedAnimal = findSignByName(czData, pillar.animal.name) || pillar.animal;
      return {
        ...pillar,
        animal: localizedAnimal,
        role: pillarRoles[index] || pillar.role,
        desc: pillarDescriptions[index] || pillar.desc,
      };
    })
    : null;

  useEffect(() => {
    if (!localizedRevealed || isReplayMode || !readingDate) return;
    setDailyReading(buildDailyReadingForPillars(localizedRevealed, readingDate));
  }, [locale, localizedRevealed, buildDailyReadingForPillars, isReplayMode, readingDate]);

  function exportChineseReadingToPdf() {
    if (!localizedRevealed) return;
    const now = new Date();
    const exportFileName = createPdfFileName("chinese-zodiac-reading", now);
    const exportedAt = now.toLocaleString();
    const monthLabel = monthLabels[month - 1];
    const hourPeriod = HOUR_PERIODS[getHourPeriodIndex(hour)];
    const pillars = localizedRevealed.map((pillar, i) => ({
      label: pillarLabels[i],
      animalName: pillar.animal.label || pillar.animal.name,
      element: pillar.element,
      period: pillar.period,
      role: pillar.role,
    }));
    const synthesisParagraphs = [
      tr("pdf_cz_synthesis_paragraph_1", {
        yearElement: getLocalizedElementLabel(localizedRevealed[0].element).toLowerCase(),
        yearAnimal: localizedRevealed[0].animal.label || localizedRevealed[0].animal.name,
        yearTrait1: localizedRevealed[0].animal.traits[0].toLowerCase(),
        yearTrait2: localizedRevealed[0].animal.traits[1].toLowerCase(),
        dayElement: getLocalizedElementLabel(localizedRevealed[2].element).toLowerCase(),
        dayAnimal: localizedRevealed[2].animal.label || localizedRevealed[2].animal.name,
        dayTrait1: localizedRevealed[2].animal.traits[2].toLowerCase(),
        dayTrait2: localizedRevealed[2].animal.traits[3].toLowerCase(),
        dayTrait3: localizedRevealed[2].animal.traits[0].toLowerCase(),
      }),
      tr("pdf_cz_synthesis_paragraph_2", {
        hourAnimal: localizedRevealed[3].animal.label || localizedRevealed[3].animal.name,
        monthAnimal: localizedRevealed[1].animal.label || localizedRevealed[1].animal.name,
        monthElement: getLocalizedElementLabel(localizedRevealed[1].element).toLowerCase(),
      }),
    ];
    const elemBalance = localizedRevealed.reduce((acc, p) => {
      acc[p.element] = (acc[p.element] || 0) + 1;
      return acc;
    }, {});
    const elementBalance = ["Wood", "Fire", "Earth", "Metal", "Water"].map((el) => ({ el, count: elemBalance[el] || 0 }));
    const compatibleMatches = localizedRevealed[2].animal.compatible.map((name, i) => ({ name, tier: compatibilityTiers[i] || t("cz_compat_tier_strong_match") }));
    const html = buildChineseZodiacPdfHtml({
      exportedAt,
      birthInfo: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")} (${monthLabel}) · ${String(hour).padStart(2, "0")}:00 · ${hourPeriod}`,
      pillars,
      synthesisParagraphs,
      elementBalance,
      compatibleMatches,
      daily: dailyReading,
      labels: {
        report: t("pdf_report_title"),
        title: t("pdf_chinese_title"),
        eyebrow: t("pdf_chinese_eyebrow"),
        dailySection: t("pdf_chinese_daily_section"),
        birthDetails: t("pdf_birth_details"),
        exported: t("pdf_exported"),
        pillar: t("pdf_pillar"),
        dailyTheme: t("pdf_theme_label"),
        dailyRelationships: t("cz_daily_relationships_label"),
        dailyCareer: t("cz_daily_career_label"),
        dailyWellness: t("cz_daily_wellness_label"),
        luckyColor: t("cz_lucky_color_label"),
        avoidColor: t("cz_avoid_color_label"),
        destinySynthesis: t("pdf_destiny_synthesis"),
        elementalBalance: t("cz_elemental_balance_title"),
        elementCountOfFour: t("cz_element_count_of_four"),
        bestMatches: t("pdf_best_compatibility"),
        generatedBy: t("pdf_generated_by"),
      },
    });
    printHtmlAsPdf({ html, title: exportFileName.replace(/\.pdf$/i, "") });
  }

  const daysInMonth = new Date(year, month, 0).getDate();
  const hourLabel = (h) => {
    const period = HOUR_PERIODS[getHourPeriodIndex(h)];
    const animal = czData[getHourAnimalIndex(h)];
    return tr("cz_hour_option_label", {
      hour: h.toString().padStart(2, "0"),
      emoji: animal.emoji,
      animal: animal.label || animal.name,
      period,
    });
  };
  const elemBalance = localizedRevealed ? localizedRevealed.reduce((acc, p) => {
    acc[p.element] = (acc[p.element] || 0) + 1;
    return acc;
  }, {}) : {};
  const synthesisReadingDate = normalizeIsoDate(readingDate) || todayIsoDate();
  const synthesisDateParts = parseIsoDate(synthesisReadingDate) || parseIsoDate(todayIsoDate());
  const synthesisV2 = localizedRevealed && synthesisDateParts
    ? buildDestinySynthesisV2({
      pillars: localizedRevealed,
      readingYear: synthesisDateParts.year,
      readingMonth: synthesisDateParts.month,
      readingDay: synthesisDateParts.day,
      czData,
      tr,
      getElementLabel: getLocalizedElementLabel,
      t,
    })
    : null;
  const inputStyle = {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 10,
    color: C.textPrimary,
    padding: "11px 14px",
    fontSize: fs - 1,
    fontFamily: "'DM Sans',sans-serif",
    outline: "none",
    width: "100%",
  };
  const selectStyle = {
    ...inputStyle,
    appearance: "none",
    cursor: "pointer",
    background: "rgba(255,255,255,0.06)",
    paddingRight: 38,
    backgroundImage: "linear-gradient(45deg, transparent 50%, rgba(237,234,248,0.8) 50%), linear-gradient(135deg, rgba(237,234,248,0.8) 50%, transparent 50%)",
    backgroundPosition: "calc(100% - 18px) calc(50% - 2px), calc(100% - 12px) calc(50% - 2px)",
    backgroundSize: "6px 6px, 6px 6px",
    backgroundRepeat: "no-repeat",
  };
  const selectOptionStyle = { background: "#0c1230", color: C.textPrimary };

  return (
    <div style={{ padding: 32 }}>
      <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 40, fontWeight: 400, color: C.textPrimary, marginBottom: 8 }}>{t("cz_title_bazi")}</h1>
      <p style={{ color: C.textSecondary, fontSize: fs, marginBottom: 8 }}>
        {t("cz_intro_line_1")} <strong style={{ color: acc.primary }}>{t("cz_intro_highlight")}</strong>
      </p>
      <p style={{ color: C.textMuted, fontSize: fs - 2, marginBottom: 32 }}>
        {t("cz_intro_line_2_prefix")} <em>{t("cz_intro_line_2_hour_emphasis")}</em> {t("cz_intro_line_2_suffix")}
      </p>

      <GlassCard style={{ padding: 28, marginBottom: 36 }} glow accent={acc.primary}>
        <div style={{ fontSize: 12, color: acc.primary, letterSpacing: "0.12em", marginBottom: 20 }}>✦ {t("cz_enter_birth_details")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 16, marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("cz_birth_year")}</div>
            <select value={year} onChange={(e) => { setYear(+e.target.value); setRevealed(null); }} style={selectStyle} disabled={isReplayMode}>
              {Array.from({ length: 100 }, (_, i) => currentYear - i).map((y) => <option key={y} value={y} style={selectOptionStyle}>{y}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("cz_birth_month")}</div>
            <select value={month} onChange={(e) => { setMonth(+e.target.value); setRevealed(null); }} style={selectStyle} disabled={isReplayMode}>
              {monthLabels.map((m, i) => <option key={i} value={i + 1} style={selectOptionStyle}>{m}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 8 }}>{t("cz_birth_day")}</div>
            <select value={day} onChange={(e) => { setDay(+e.target.value); setRevealed(null); }} style={selectStyle} disabled={isReplayMode}>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => <option key={d} value={d} style={selectOptionStyle}>{d}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: acc.secondary, letterSpacing: "0.1em", marginBottom: 8 }}>
              {t("cz_birth_hour")} <span style={{ color: C.textMuted, fontSize: 10 }}>({t("cz_for_hour_pillar")})</span>
            </div>
            <select value={hour} onChange={(e) => { setHour(+e.target.value); setRevealed(null); }} style={selectStyle} disabled={isReplayMode}>
              {BIRTH_HOUR_VALUES.map((h) => <option key={h} value={h} style={selectOptionStyle}>{hourLabel(h)}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {!isReplayMode && (
            <Button variant="primary" acc={acc} onClick={requestReveal} disabled={animating || isSubmitting || isGenerateLockedByEntitlement} style={{ padding: "13px 40px", fontSize: fs }}>
              {animating || isSubmitting ? t("cz_reading_cosmic_pillars") : revealButtonLabel}
            </Button>
          )}
          {!isReplayMode && revealed && !animating && (
            <div style={{ fontSize: 13, color: C.textMuted }}>
              <span style={{ color: acc.primary }}>✦</span> {tr("cz_credits_used_dynamic", { credits: chineseCreditCost || 2 })}
            </div>
          )}
          {isReplayMode && (
            <div style={{ fontSize: 13, color: C.textMuted }}>
              <span style={{ color: acc.primary }}>✦</span> {loadingReplay ? t("cz_loading_replay") : t("dashboard_replay_mode_badge")}
            </div>
          )}
          {isReplayMode && !loadingReplay && (
            <Button
              variant="primary"
              acc={acc}
              onClick={() => (onResetPage ? onResetPage() : window.location.assign(localizedPath("chinese", locale)))}
              style={{ padding: "12px 24px", fontSize: fs - 1 }}
            >
              {t("dashboard_replay_start_new_chinese")}
            </Button>
          )}
          {revealed && <Button variant="outline" acc={acc} onClick={exportChineseReadingToPdf}>{t("export_result_pdf")}</Button>}
        </div>
      </GlassCard>

      {animating && (
        <GlassCard style={{ padding: 28, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: acc.primary, boxShadow: `0 0 12px ${acc.primary}` }} />
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: C.textPrimary }}>{t("cz_reading_cosmic_pillars")}</div>
          </div>
          <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 14 }}>{t("cz_loading_details")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={`cz-loading-card-${i}`}
                style={{
                  height: i < 4 ? 92 : 72,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "linear-gradient(90deg, rgba(255,255,255,0.03), rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
                }}
              />
            ))}
          </div>
        </GlassCard>
      )}

      {(localizedRevealed || animating) && (
        <div>
          {localizedRevealed && (
            <>
              <div style={{ fontSize: 12, letterSpacing: "0.15em", color: C.textMuted, textTransform: "uppercase", marginBottom: 20 }}>✦ {t("cz_four_pillars_section_title")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 20, marginBottom: 32 }}>
                {localizedRevealed.map((pillar, i) => (
                  <PillarCard
                    key={i}
                    pillar={pillar}
                    index={i}
                    fs={fs}
                    revealed={pillarsReady}
                    t={t}
                    pillarLabels={pillarLabels}
                    onClick={() => openDetailModal({ type: "pillar", index: i })}
                  />
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: responsiveGridColumns(isMobile, { desktop: "1fr 300px", mobile: "1fr" }), gap: 20, marginBottom: 40 }}>
                <GlassCard style={{ padding: 28 }} glow accent={acc.primary}>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, color: C.textPrimary, marginBottom: 16 }}>{t("cz_destiny_synthesis_title")}</div>
                  {synthesisV2 && (
                    <>
                      <p style={{ fontSize: 11, color: C.textMuted, marginBottom: 14, lineHeight: 1.6 }}>{t("cz_synthesis_birth_foundation_note")}</p>
                      <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.1em", marginBottom: 6 }}>{t("cz_synthesis_v2_core_pattern_title")}</div>
                      <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.8, marginBottom: 14 }}>{synthesisV2.corePattern}</p>
                      <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.1em", marginBottom: 6 }}>{t("cz_synthesis_v2_tension_strength_title")}</div>
                      <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.8, marginBottom: 14 }}>{synthesisV2.tensionStrength}</p>
                      <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.1em", marginBottom: 6 }}>{t("cz_synthesis_v2_practical_guidance_title")}</div>
                      <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.8 }}>{synthesisV2.practicalGuidance}</p>
                    </>
                  )}
                </GlassCard>

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <GlassCard style={{ padding: 24 }}>
                    <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.12em", marginBottom: 16 }}>{t("cz_elemental_balance_title")}</div>
                    {["Wood", "Fire", "Earth", "Metal", "Water"].map((el) => {
                      const count = elemBalance[el] || 0;
                      const ec = ELEM_COLORS[el];
                      return (
                        <div key={el} style={{ marginBottom: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                            <span style={{ color: ec }}>{getLocalizedElementLabel(el)}</span>
                            <span style={{ color: C.textMuted }}>{tr("cz_element_count_of_four", { count })}</span>
                          </div>
                          <div style={{ height: 5, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                            <div style={{ width: `${(count / 4) * 100}%`, height: "100%", borderRadius: 3, background: ec, boxShadow: `0 0 8px ${ec}88`, transition: "width 1s ease" }} />
                          </div>
                        </div>
                      );
                    })}
                  </GlassCard>

                  <GlassCard style={{ padding: 20 }}>
                    <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.12em", marginBottom: 12 }}>{t("cz_true_self_compatible_title")}</div>
                    {localizedRevealed[2].animal.compatible.map((name, i) => {
      const match = findSignByName(czData, name);
                      const mc = ELEM_COLORS[match?.element] || acc.primary;
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, background: `${mc}10`, border: `1px solid ${mc}30`, marginBottom: 8 }}>
                          <span style={{ fontSize: 20 }}>{match?.emoji}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, color: C.textPrimary }}>{match?.label || name}</div>
                            <div style={{ fontSize: 11, color: mc }}>{compatibilityTiers[i]}</div>
                          </div>
                          <span style={{ color: mc, fontSize: 12 }}>{"♡".repeat(3 - i)}</span>
                        </div>
                      );
                    })}
                  </GlassCard>
                </div>
              </div>

              {dailyReading && (
                <GlassCard style={{ padding: 24, marginBottom: 32 }} glow accent={acc.secondary}>
                  <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.12em", marginBottom: 10 }}>{t("cz_daily_reading_title")}</div>
                  <div style={{ fontSize: 12, color: acc.secondary, marginBottom: 10 }}>
                    {tr("cz_daily_for_date", { date: formatReadingDateLabel(normalizeIsoDate(readingDate) || normalizeIsoDate(dailyReading.date)) })}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                    {dailyReading.focusArea && (
                      <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.08em", padding: "4px 10px", borderRadius: 999, background: `${acc.primary}18`, border: `1px solid ${acc.primary}44` }}>
                        {t("cz_daily_focus_label")} {t(`cz_daily_focus_${dailyReading.focusArea}`)}
                      </div>
                    )}
                    {dailyReading.branchRelation && (
                      <div style={{ fontSize: 11, color: acc.secondary, letterSpacing: "0.06em", padding: "4px 10px", borderRadius: 999, background: `${acc.secondary}14`, border: `1px solid ${acc.secondary}40` }}>
                        {t(`cz_daily_branch_chip_${dailyReading.branchRelation}`)}
                      </div>
                    )}
                  </div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: C.textPrimary, marginBottom: 8 }}>{dailyReading.summary}</div>
                  {dailyReading.whyLine && (
                    <p style={{ fontSize: 12, color: acc.secondary, lineHeight: 1.6, marginBottom: 16 }}>{dailyReading.whyLine}</p>
                  )}
                  <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 6 }}>{t("cz_daily_relationships_label")}</div>
                  <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.7, marginBottom: 14 }}>{dailyReading.relationships}</p>
                  <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 6 }}>{t("cz_daily_career_label")}</div>
                  <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.7, marginBottom: 14 }}>{dailyReading.career}</p>
                  <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 6 }}>{t("cz_daily_wellness_label")}</div>
                  <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.7, marginBottom: dailyReading.doToday || dailyReading.avoidToday ? 12 : 0 }}>{dailyReading.wellness}</p>
                  {(dailyReading.doToday || dailyReading.avoidToday) && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginBottom: 14 }}>
                      {dailyReading.doToday && (
                        <div style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                          <div style={{ fontSize: 10, color: acc.secondary, letterSpacing: "0.1em", marginBottom: 4 }}>{t("cz_daily_do_today_label")}</div>
                          <div style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.6 }}>{dailyReading.doToday}</div>
                        </div>
                      )}
                      {dailyReading.avoidToday && (
                        <div style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                          <div style={{ fontSize: 10, color: C.textMuted, letterSpacing: "0.1em", marginBottom: 4 }}>{t("cz_daily_avoid_today_label")}</div>
                          <div style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.6 }}>{dailyReading.avoidToday}</div>
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: C.textMuted }}>{t("cz_lucky_color_label")} <span style={{ color: CZ_COLOR_HEX[dailyReading.luckyColorKey] || C.textPrimary }}>{dailyReading.luckyColor}</span></div>
                    <div style={{ fontSize: 12, color: C.textMuted }}>{t("cz_avoid_excess_label")} <span style={{ color: CZ_COLOR_HEX[dailyReading.avoidColorKey] || C.textPrimary }}>{dailyReading.avoidColor}</span></div>
                  </div>
                </GlassCard>
              )}
            </>
          )}
        </div>
      )}

      {!localizedRevealed && !animating && (
        <>
          <div style={{ fontSize: 12, letterSpacing: "0.15em", color: C.textMuted, textTransform: "uppercase", marginBottom: 16 }}>{t("cz_all_twelve_signs")}</div>
          <div className="ecliptica-card-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 12 }}>
            {czData.map((a, i) => (
              <ChineseSignCell
                key={a.name}
                sign={a}
                index={i}
                elementColor={ELEM_COLORS[a.element]}
                elementLabel={getLocalizedElementLabel(a.element)}
                onSelect={(index) => openDetailModal({ type: "sign", index })}
              />
            ))}
          </div>
        </>
      )}

      {detailModal && (() => {
        const isSignModal = detailModal.type === "sign";
        const sign = isSignModal ? czData[detailModal.index] : (localizedRevealed?.[detailModal.index]?.animal || null);
        const pillar = !isSignModal ? (localizedRevealed?.[detailModal.index] || null) : null;
        if (!sign && !pillar) return null;
        const modalElement = isSignModal ? sign.element : pillar.element;
        const modalColor = ELEM_COLORS[modalElement] || acc.primary;
        const modalTitle = isSignModal
          ? (sign.label || sign.name)
          : interpolate(t("cz_pillar_label_with_suffix"), { label: pillarLabels[detailModal.index] });
        return (
          <div
            onClick={closeDetailModal}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(3,6,20,0.72)",
              backdropFilter: "blur(4px)",
              zIndex: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "min(720px, 100%)",
                maxHeight: "85vh",
                overflowY: "auto",
                borderRadius: 20,
                padding: 24,
                background: "rgba(10,16,38,0.96)",
                border: `1px solid ${modalColor}66`,
                boxShadow: `0 20px 60px rgba(0,0,0,0.45), 0 0 28px ${modalColor}44`,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 30, color: C.textPrimary }}>{modalTitle}</div>
                  <div style={{ fontSize: 12, color: modalColor }}>
                    {isSignModal ? tr("cz_element_suffix", { element: getLocalizedElementLabel(sign.element) }) : `${pillar.period} · ${pillar.role}`}
                  </div>
                </div>
                <button type="button" onClick={closeDetailModal} style={{ border: "none", background: "transparent", color: C.textMuted, fontSize: 24, cursor: "pointer", padding: 0 }}>×</button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 36 }}>{(isSignModal ? sign.emoji : pillar.animal.emoji)}</div>
                <div style={{ fontSize: 15, color: C.textSecondary }}>
                  {isSignModal ? sign.desc : pillar.desc}
                </div>
              </div>

              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>
                {t("cz_traits_label")} {(isSignModal ? sign.traits : pillar.animal.traits).join(" · ")}
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>
                {t("cz_most_compatible_with_label")} {(isSignModal ? sign.compatible : pillar.animal.compatible).map((name) => findSignByName(czData, name)?.label || name).join(", ")}
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>
                {t("cz_lucky_colors_label")} {(isSignModal ? sign.lucky : pillar.animal.lucky).map((color, i) => {
                  const key = (isSignModal ? sign.luckyColorKeys : pillar.animal.luckyColorKeys)?.[i];
                  const hex = CZ_COLOR_HEX[key] || C.textPrimary;
                  return <span key={`lucky-${key || i}`} style={{ color: hex }}>{`${i ? ", " : ""}${color}`}</span>;
                })}
                {" · "}
                {t("cz_avoid_label")} {(isSignModal ? sign.avoid : pillar.animal.avoid).map((color, i) => {
                  const key = (isSignModal ? sign.avoidColorKeys : pillar.animal.avoidColorKeys)?.[i];
                  const hex = CZ_COLOR_HEX[key] || C.textPrimary;
                  return <span key={`avoid-${key || i}`} style={{ color: hex }}>{`${i ? ", " : ""}${color}`}</span>;
                })}
              </div>
              <div style={{ fontSize: 12, color: C.textMuted }}>
                {t("cz_recent_years_label")} {getRecentYearsForSign((isSignModal ? sign.name : pillar.animal.name), currentYear).join(", ")}
              </div>
            </div>
          </div>
        );
      })()}

      <FreeQuotaConfirmPopup
        open={showFreeQuotaPopup}
        title={getFreeQuotaPopupCopy("chinese", t).title}
        message={getFreeQuotaPopupCopy("chinese", t).message}
        note={getFreeQuotaPopupCopy("chinese", t).note}
        confirmLabel={t("free_quota_popup_confirm")}
        cancelLabel={t("free_quota_popup_cancel")}
        onClose={() => setShowFreeQuotaPopup(false)}
        onConfirm={() => {
          setShowFreeQuotaPopup(false);
          requestReveal({ skipFreeQuotaGuard: true });
        }}
        acc={acc}
      />
      <PredictionAccessPopup
        open={showAccessPopup}
        title={accessPopupCopy.title}
        message={accessPopupCopy.message}
        onClose={() => setShowAccessPopup(false)}
        onGoToPricing={() => {
          window.location.href = localizedPath("creditsPlans", locale);
        }}
        acc={acc}
      />
    </div>
  );
}
